// prefsStore — single storage-routing layer for all `nomloop.*` client state.
//
// Why this exists:
//   NomLoop persists ~16 onboarding/plan slots to web storage. Those slots must
//   land in DIFFERENT places depending on who is using the app:
//
//     authenticated  → localStorage  (survives reload + browser close; backed by
//                                      the server profile/plan for cross-browser)
//     tasting (guest) → sessionStorage (survives reload, DIES on tab/browser close
//                                       — the "Tasting Stage won't be remembered"
//                                       guarantee comes for free from the browser)
//     unauthenticated → none         (reads return defaults, writes are dropped —
//                                      the entry screen must never hydrate or leak
//                                      a prior user's data)
//
//   Routing every read/write through one module means:
//     • one place decides local vs session vs none (no scattered localStorage refs)
//     • clear-on-logout / clear-on-exit is a single call, not 16 hand-edits
//     • it is unit-testable in isolation (see __tests__/prefsStore.test.js)
//
//   IMPORTANT: this only routes storage. It does NOT reset React in-memory state —
//   the App must remount (key={authScope}) on every auth transition so the prior
//   user's values don't linger in RAM. clearAll() wipes disk, not memory.
//
//   ASCII — mode → backing store:
//
//     setMode('authenticated') ─► localStorage  ┐
//     setMode('tasting')       ─► sessionStorage ├─► read()/write() target
//     setMode('unauthenticated')─► null (no-op)  ┘
//
//   All access is wrapped in try/catch: Safari Private Mode and some embedded
//   webviews throw on storage access, and a thrown getItem must degrade to the
//   caller's fallback, never crash the app.

const PREFIX = "nomloop.";
// Tasting-session marker. Lives in sessionStorage so it (and therefore Tasting
// Stage) evaporates when the tab/browser closes. Prefixed with `nomloop.` so the
// session clear sweeps it away too.
export const TASTING_FLAG = "nomloop.tasting";

// `active` is the storage currently routed to. null = "none" mode (no-op).
// Module-level singleton: there is exactly one App instance per document. Tests
// reset it via setMode() in a beforeEach.
let active = null;

function safeLocal() {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}
function safeSession() {
  try { return typeof window !== "undefined" ? window.sessionStorage : null; } catch { return null; }
}

/**
 * Point the store at the backing storage for the given auth status. Call this
 * synchronously at the top of App() BEFORE any useState initializer runs, so the
 * initializers read from the correct store on first render.
 * @param {"authenticated"|"tasting"|"unauthenticated"|"loading"|"beta_pending"} status
 */
export function setMode(status) {
  active =
    status === "authenticated" ? safeLocal() :
    status === "tasting"       ? safeSession() :
    null;
}

/** Current mode, for assertions/tests. */
export function getMode() {
  if (active && active === safeLocal()) return "local";
  if (active && active === safeSession()) return "session";
  return "none";
}

/**
 * Read and JSON-parse a value. Returns `fallback` in none-mode, on a missing
 * key, or on any storage/parse error.
 */
export function read(key, fallback) {
  if (!active) return fallback;
  try {
    const v = active.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
}

/** JSON-serialise and write a value. No-op in none-mode or on storage error. */
export function write(key, value) {
  if (!active) return;
  try {
    active.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private-mode — drop silently, same as the legacy try/catch sites */
  }
}

// Remove every `nomloop.*` key from a specific storage. Iterates a snapshot of
// keys first because removeItem() mutates the live index mid-loop.
function clearPrefixed(storage) {
  if (!storage) return;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      try { storage.removeItem(k); } catch { /* ignore individual key failure */ }
    }
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Clear all `nomloop.*` keys from localStorage. Used on authenticated logout so
 * the next user on a shared browser inherits nothing. NEVER touches the backend.
 * Only removes prefixed keys — unrelated localStorage (cookie consent, etc.) is
 * left intact.
 */
export function clearLocal() {
  clearPrefixed(safeLocal());
}

/**
 * Clear all `nomloop.*` keys from sessionStorage (including the tasting flag).
 * Used on "Exit Tasting Stage".
 */
export function clearSession() {
  clearPrefixed(safeSession());
}

// ── Tasting-session flag (sessionStorage only) ────────────────────────────────

/** True if a Tasting Stage session is active in this tab. */
export function hasTastingSession() {
  try {
    return safeSession()?.getItem(TASTING_FLAG) === "1";
  } catch {
    return false;
  }
}

/** Mark this tab as a Tasting Stage session. */
export function beginTastingSession() {
  try { safeSession()?.setItem(TASTING_FLAG, "1"); } catch { /* private mode */ }
}

/** End the Tasting Stage session: drop the flag and all tasting data. */
export function endTastingSession() {
  clearSession();
}
